package com.evoapps.evoshift.ui.checklists

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.toggleable
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.ChecklistItem
import com.evoapps.evoshift.domain.model.ChecklistKind
import com.evoapps.evoshift.domain.model.ShiftChecklist
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.checklist_complete
import evoshift.composeapp.generated.resources.checklist_kind_closing
import evoshift.composeapp.generated.resources.checklist_kind_custom
import evoshift.composeapp.generated.resources.checklist_kind_opening
import evoshift.composeapp.generated.resources.checklist_not_started
import evoshift.composeapp.generated.resources.checklist_progress
import evoshift.composeapp.generated.resources.checklist_skipped_on_tablet
import evoshift.composeapp.generated.resources.checklist_skipped_on_tablet_at
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Instant

/*
 * SH-121: pieces the checklist screens share: labels, the progress line and the item row.
 */

/** Ticked items and done checklists. */
internal val ChecklistDoneColor = Color(0xFF2E7D32)

/** Test tags (robots find rows by them). */
fun checklistTemplateTag(templateId: String) = "checklist_template_$templateId"
fun checklistItemTag(checklistId: String, itemId: String) = "checklist_item_${checklistId}_$itemId"
fun checklistDayTag(shiftId: String) = "checklist_day_$shiftId"
fun myChecklistTag(checklistId: String) = "my_checklist_$checklistId"
fun atWorkChecklistsTag(shiftId: String) = "at_work_checklists_$shiftId"

val ChecklistKind.label: StringResource
    get() = when (this) {
        ChecklistKind.OPENING -> Res.string.checklist_kind_opening
        ChecklistKind.CLOSING -> Res.string.checklist_kind_closing
        ChecklistKind.CUSTOM -> Res.string.checklist_kind_custom
    }

/** "Done", "Not started" or "2/5 done". */
@Composable
fun checklistStatus(checklist: ShiftChecklist): String = when {
    checklist.isComplete -> stringResource(Res.string.checklist_complete)
    !checklist.isStarted -> stringResource(Res.string.checklist_not_started)
    else -> stringResource(Res.string.checklist_progress, checklist.doneCount, checklist.items.size)
}

/** A checklist's name with its kind and where it stands, on a light card. */
@Composable
fun ChecklistHeader(checklist: ShiftChecklist, modifier: Modifier = Modifier) {
    Row(modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        Column(Modifier.weight(1f)) {
            Text(checklist.name, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy)
            Text(stringResource(checklist.kind.label), style = MaterialTheme.typography.labelMedium, color = DeepNavy.copy(alpha = 0.6f))
        }
        if (checklist.isComplete) {
            Icon(Icons.Default.CheckCircle, contentDescription = null, tint = ChecklistDoneColor, modifier = Modifier.size(18.dp))
            Spacer(Modifier.width(4.dp))
        }
        Text(
            checklistStatus(checklist),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = if (checklist.isComplete) ChecklistDoneColor else DeepNavy.copy(alpha = 0.75f)
        )
    }
}

/** "HH:mm" of an instant in the device's zone; null when unreadable. */
internal fun checklistLocalTime(instant: String): String? = runCatching {
    val t = Instant.parse(instant).toLocalDateTime(TimeZone.currentSystemDefault())
    "${t.hour.toString().padStart(2, '0')}:${t.minute.toString().padStart(2, '0')}"
}.getOrNull()

/**
 * SH-127/136: "Skipped on the tablet at 22:05" when the person went on without finishing the
 * checklist on the clock-in tablet (only the server records a skip). Nothing otherwise.
 */
@Composable
fun ChecklistSkippedLine(checklist: ShiftChecklist, modifier: Modifier = Modifier) {
    val at = checklist.skippedAt ?: return
    val time = checklistLocalTime(at)
    Text(
        if (time != null) stringResource(Res.string.checklist_skipped_on_tablet_at, time) else stringResource(Res.string.checklist_skipped_on_tablet),
        style = MaterialTheme.typography.labelMedium,
        fontWeight = FontWeight.Bold,
        color = ChecklistSkippedColor,
        modifier = modifier
    )
}

/** A checklist skipped on the tablet (amber, like other things a manager should look at). */
internal val ChecklistSkippedColor = Color(0xFFB26A00)

/**
 * One task with its tick and note. [onToggle] null: read only (the managers' view, or someone
 * else's shift); [trailing] adds e.g. a note button.
 */
@Composable
fun ChecklistItemRow(
    checklist: ShiftChecklist,
    item: ChecklistItem,
    onToggle: (() -> Unit)?,
    trailing: @Composable (() -> Unit)? = null
) {
    val done = checklist.isDone(item.id)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 48.dp)
            .testTag(checklistItemTag(checklist.id, item.id))
            .then(
                if (onToggle != null) Modifier.handCursor().toggleable(value = done, role = Role.Checkbox, onValueChange = { onToggle() })
                else Modifier
            )
            .padding(vertical = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Checkbox(
            checked = done,
            onCheckedChange = null,
            enabled = onToggle != null || done,
            colors = CheckboxDefaults.colors(
                checkedColor = DarkGold,
                uncheckedColor = DeepNavy.copy(alpha = 0.5f),
                checkmarkColor = DeepNavy,
                disabledCheckedColor = ChecklistDoneColor.copy(alpha = 0.8f)
            )
        )
        Column(Modifier.weight(1f)) {
            Text(
                item.text,
                style = MaterialTheme.typography.bodyLarge,
                color = DeepNavy.copy(alpha = if (done) 0.6f else 1f),
                textDecoration = if (done) TextDecoration.LineThrough else null
            )
            checklist.noteOf(item.id)?.let {
                Text(it, style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.75f))
            }
        }
        trailing?.invoke()
    }
}
